import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatCLP } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BadgeCheck, Clock, FileText, Handshake } from "lucide-react";

const MODALITY_LABEL = {
  frustrated: "Mediación frustrada",
  agreement: "Con acta de acuerdo",
};

const STATUS_LABEL = {
  pending: { label: "Pendiente de aceptación", cls: "badge-pending" },
  accepted: { label: "Aceptada · pago pendiente", cls: "badge-cyan" },
  in_progress: { label: "En proceso", cls: "badge-cyan" },
  awaiting_approval: { label: "Acta enviada · esperando aprobación", cls: "badge-pending" },
  completed: { label: "Completada", cls: "badge-verified" },
  cancelled: { label: "Cancelada", cls: "" },
  rejected: { label: "Rechazada", cls: "" },
};

export default function Mediations() {
  const { user } = useAuth();
  const [tab, setTab] = useState(user?.role === "mediator" ? "mediating" : "requested");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mediations", { params: { mine: tab } });
      setItems(data.items);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  return (
    <div className="container-app py-12" data-testid="mediations-page">
      <div className="label-eyebrow">Mediaciones</div>
      <h1 className="serif text-5xl mt-3">Mis mediaciones</h1>

      <div className="mt-8 flex gap-2 flex-wrap">
        {user?.role === "lawyer" && (
          <button onClick={()=>setTab("requested")} className={`pill ${tab === "requested" ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-transparent" : ""}`} data-testid="tab-requested">
            Solicité ({tab === "requested" ? items.length : "—"})
          </button>
        )}
        {user?.role === "mediator" && (
          <button onClick={()=>setTab("mediating")} className={`pill ${tab === "mediating" ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-transparent" : ""}`} data-testid="tab-mediating">
            Me solicitaron ({tab === "mediating" ? items.length : "—"})
          </button>
        )}
        {user?.role === "lawyer" && (
          <Link to="/app/mediadores" className="btn-ghost text-sm py-2 ml-auto" data-testid="cta-find-mediator">Buscar mediador →</Link>
        )}
      </div>

      <ul className="mt-6 space-y-3" data-testid="mediations-list">
        {loading && <li className="panel p-10 text-center text-muted-foreground">Cargando…</li>}
        {!loading && items.length === 0 && (
          <li className="panel p-10 text-center text-muted-foreground">
            {tab === "requested"
              ? <>Aún no has solicitado mediaciones. <Link to="/app/mediadores" className="link-underline text-foreground">Ver directorio</Link></>
              : "Aún no te han solicitado mediaciones."}
          </li>
        )}
        {items.map((m) => {
          const st = STATUS_LABEL[m.status] || { label: m.status, cls: "" };
          return (
            <li key={m.id} className="panel p-6 hover:border-foreground/40 transition-colors" data-testid={`mediation-row-${m.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/app/mediaciones/${m.id}`} className="serif text-xl link-underline">{m.subject}</Link>
                    <span className={`pill ${st.cls}`}>{st.label}</span>
                    <span className="pill"><Handshake size={12}/>{MODALITY_LABEL[m.modality]}</span>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {tab === "requested" ? <>Con <strong>{m.mediator_name}</strong> · {m.mediator_comuna}, {m.mediator_region}</> : <>De <strong>{m.requester_name}</strong></>}
                  </div>
                  {m.parties_description && <p className="mt-2 text-sm text-foreground/80 line-clamp-1">{m.parties_description}</p>}
                </div>
                <div className="text-right">
                  <div className="serif text-2xl">{formatCLP(m.fee_clp)}</div>
                  {m.payment_status === "in_escrow" && <span className="pill badge-cyan mt-2 inline-flex">Pago Protegido activo</span>}
                  {m.payment_status === "released" && <span className="pill badge-verified mt-2 inline-flex">Pago liberado</span>}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
